import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from '@hello-pangea/dnd';
import { Bars2Icon } from '@heroicons/react/24/solid';
import { ReactElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../utils';

interface SortableListItem {
  id: string;
}

interface SortableListProps<T> {
  items: (T & SortableListItem)[];
  renderItemContent: (
    item: T & SortableListItem,
    index: number,
  ) => ReactElement<any>;
  renderActions?: (
    item: T & SortableListItem,
    index: number,
  ) => ReactElement<any>;
  onChange: (items: (T & SortableListItem)[]) => void;
  itemClassName?:
    | string
    | ((
        item: T & SortableListItem,
        index: number,
        isDragging: boolean,
      ) => string);
  hideDivider?: boolean;
  disabledIds?: string[];
  hideDragHandle?: boolean;
}

export default function SortableList<T>({
  items,
  renderItemContent,
  onChange,
  renderActions,
  itemClassName,
  hideDivider = false,
  disabledIds,
  hideDragHandle = false,
}: SortableListProps<T>) {
  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) return;

    const _items = Array.from(items);
    const [reorderedItem] = _items.splice(result.source.index, 1);
    _items.splice(result.destination.index, 0, reorderedItem);

    onChange(_items);
  };

  const [portal, setPortal] = useState();

  useEffect(() => {
    setPortal(document.body as any);
  }, []);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, droppableSnapshot) => (
          <ul
            role="list"
            className={!hideDivider ? 'divide-y divide-gray-100' : undefined}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {items.map((item, index) => (
              <Draggable
                key={item.id}
                draggableId={item.id}
                index={index}
                isDragDisabled={disabledIds?.includes(item.id)}
              >
                {(provided, snapshot) => {
                  const usePortal = snapshot.isDragging;

                  const child = (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...(hideDragHandle ? provided.dragHandleProps : {})}
                      className={classNames(
                        'group/item relative flex min-h-[42px] items-center gap-x-6 px-1 py-1',
                        !!snapshot.isDragging &&
                          classNames(
                            'bg-white',
                            !hideDivider && 'border-y border-gray-200',
                          ),
                        hideDragHandle && !disabledIds?.includes(item.id)
                          ? 'cursor-grab'
                          : '',
                        itemClassName
                          ? typeof itemClassName === 'string'
                            ? itemClassName
                            : itemClassName(item, index, snapshot.isDragging)
                          : undefined,
                      )}
                    >
                      <div className="flex flex-1">
                        {/* Drag handle */}
                        {!hideDragHandle && (
                          <div
                            {...provided.dragHandleProps}
                            className={classNames(
                              'mr-3 flex items-center justify-center',
                              disabledIds?.includes(item.id)
                                ? 'cursor-not-allowed'
                                : 'cursor-grab',
                            )}
                          >
                            <Bars2Icon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}

                        {/* Rest of the list item content */}
                        <div className="flex w-full min-w-0 justify-stretch gap-x-4">
                          {renderItemContent(item, index)}
                        </div>
                      </div>
                      {renderActions ? (
                        <div
                          className={classNames(
                            'invisible absolute right-0 flex items-center gap-1',
                            droppableSnapshot.draggingFromThisWith
                              ? ''
                              : 'group-hover/item:visible',
                          )}
                        >
                          {renderActions(item, index)}
                        </div>
                      ) : (
                        <></>
                      )}
                    </li>
                  );

                  if (!usePortal) {
                    return child;
                  }

                  return createPortal(child, portal as any);
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
}
